import React from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { alpha, Theme } from "@mui/material/styles";
import { getBrandLogoProps, hasBrandLogo } from "../utils/brandLogoUtils";
import { AggregatedBrand, BrandTab } from "../hooks/usePageBrandMentions";

interface ReviewBrandFilterBarProps {
  brandTab: BrandTab;
  onBrandTabChange: (tab: BrandTab) => void;
  brands: AggregatedBrand[];
  selectedBrandName: string | null;
  onBrandSelect: (brandName: string | null) => void;
  onBrandEdit: (
    brandName: string,
    brandId: string | undefined,
    aliases: string[],
    mentionCount: number,
  ) => void;
  getClassificationColor: (classification: string) => string;
  isLoading: boolean;
  validBrandCount: number;
  invalidBrandCount: number;
  brandSearchQuery: string;
  onBrandSearchChange: (query: string) => void;
}

const brandPillSx = (theme: Theme, selected: boolean) => ({
  width: 140,
  flexShrink: 0,
  position: "relative" as const,
  p: 1.25,
  border: 1,
  borderColor: selected ? "primary.main" : "divider",
  borderRadius: 2,
  cursor: "pointer",
  textAlign: "center" as const,
  bgcolor: selected
    ? theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.08)
    : "transparent",
  transition: "border-color 0.15s, background-color 0.15s",
  "&:hover": {
    borderColor: selected ? "primary.main" : "text.disabled",
    bgcolor: selected
      ? undefined
      : theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.04)
        : alpha(theme.palette.common.black, 0.03),
  },
});

const ReviewBrandFilterBar: React.FC<ReviewBrandFilterBarProps> = ({
  brandTab,
  onBrandTabChange,
  brands,
  selectedBrandName,
  onBrandSelect,
  onBrandEdit,
  getClassificationColor,
  isLoading,
  validBrandCount,
  invalidBrandCount,
  brandSearchQuery,
  onBrandSearchChange,
}) => {
  const handleTabChange = (_: React.SyntheticEvent, value: BrandTab) => {
    onBrandTabChange(value);
  };

  const handlePillClick = (brandName: string) => {
    onBrandSelect(selectedBrandName === brandName ? null : brandName);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Tabs
          value={brandTab}
          onChange={handleTabChange}
          sx={{ minHeight: 40 }}
        >
          <Tab
            value="valid"
            label={`Valid (${validBrandCount})`}
            sx={{ minHeight: 40, py: 0.5 }}
          />
          <Tab
            value="invalid"
            label={`Invalid (${invalidBrandCount})`}
            sx={{ minHeight: 40, py: 0.5 }}
          />
          <Tab value="all" label="All" sx={{ minHeight: 40, py: 0.5 }} />
        </Tabs>
        <TextField
          size="small"
          placeholder="Filter brands..."
          value={brandSearchQuery}
          onChange={(e) => onBrandSearchChange(e.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 220 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: brandSearchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onBrandSearchChange("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      {selectedBrandName && (
        <Chip
          label={`Filtering: ${selectedBrandName}`}
          onDelete={() => onBrandSelect(null)}
          color="primary"
          size="small"
          sx={{ mb: 1.5 }}
        />
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : brands.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, textAlign: "center" }}
        >
          No brands found for this tab
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 0.5,
            scrollSnapType: "x mandatory",
            "& > *": { scrollSnapAlign: "start" },
          }}
        >
          {brands.map((brand) => {
            const selected = selectedBrandName === brand.brandName;
            return (
              <Box
                key={brand.brandName}
                onClick={() => handlePillClick(brand.brandName)}
                sx={(theme) => brandPillSx(theme, selected)}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    p: 0.25,
                    color: "text.secondary",
                  }}
                  title="Manage brand"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBrandEdit(
                      brand.brandName,
                      brand.brandId,
                      brand.aliases,
                      brand.count,
                    );
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={0.75}
                >
                  {hasBrandLogo(brand) ? (
                    <Avatar
                      {...getBrandLogoProps(brand)}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.light" }}>
                      <BusinessIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    textAlign="center"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.2,
                      pr: 2,
                      textDecoration: brand.isValid ? "none" : "line-through",
                      color: brand.isValid ? "text.primary" : "text.disabled",
                    }}
                  >
                    {brand.brandName}
                  </Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="center">
                    <Chip
                      label={brand.classification}
                      size="small"
                      sx={{
                        bgcolor: getClassificationColor(brand.classification),
                        color: "white",
                        fontSize: "0.6rem",
                        height: 18,
                      }}
                    />
                    <Chip
                      label={brand.count}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem", height: 18, minWidth: 24 }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ReviewBrandFilterBar;
